<template>
  <BaseWidget 
    title="Your tasks"
    :show-settings="false"
    :disable-date-range="true"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :show-info-icon="true"
    info-tooltip="Tasks assigned to you. Create and manage tasks from the tasks page."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/9118995-how-do-i-create-a-task', text: 'Learn about Tasks' }"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading || loadingUser"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error || userError"
      class="error-state"
    >
      Error: {{ error?.message || userError?.message || 'Failed to load tasks' }}
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <MetricValue 
        :value="taskCount.toString()" 
        subtitle="To do"
        :has-data-table="true"
      >
        <template #comparison>
          <span
            v-if="pastDueCount > 0"
            class="past-due-text"
          >{{ pastDueCount }} past due</span>
        </template>
        <template #subtitle-suffix>
          <a
            v-if="tasks.length > 0"
            class="focused-view-all-link"
            @click.prevent="handleViewAllClick"
          >
            View all
          </a>
        </template>
      </MetricValue>
      
      <div
        v-if="!focused && tasks.length > 0"
        class="tasks-table"
        @mousedown.stop
      >
        <table>
          <tbody>
            <tr 
              v-for="task in displayedTasks" 
              :key="task.task_id" 
              class="task-row" 
              :class="{ 
                'task-completed': completedTasks.has(task.task_id),
                'task-fading': fadingTasks.has(task.task_id)
              }"
              @click="handleTaskClickGuarded(task)" 
              @mousedown.stop
            >
              <td class="checkbox-cell">
                <button
                  class="task-checkbox"
                  :disabled="markingTaskId === task.task_id"
                  :aria-label="`Mark ${task.name} as done`"
                  @click.stop="markTaskAsDone(task)"
                  @mousedown.stop
                >
                  <span
                    v-if="completedTasks.has(task.task_id)"
                    class="material-icons checkbox-icon checkbox-checked"
                  >check_circle</span>
                  <span
                    v-else
                    class="material-icons-outlined checkbox-icon"
                  >radio_button_unchecked</span>
                </button>
              </td>
              <td
                class="task-name-cell"
                :class="{ 'task-strikethrough': completedTasks.has(task.task_id) }"
              >
                <div class="task-name">
                  {{ task.name }}
                </div>
                <div
                  v-if="task.due_timestamp"
                  class="task-due-date"
                  :class="{ 'past-due': isPastDue(task.due_timestamp) }"
                >
                  {{ formatDueDate(task.due_timestamp) }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-else-if="!focused"
        class="no-tasks"
      >
        No tasks currently due
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { useDemoMode } from '../composables/useDemoMode.js'
import { getDemoTasksData } from '../services/demoData.js'
import { executeGraphQLQuery } from '../services/optixApi.js'
import { useSnackbar } from '../composables/useSnackbar.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget', 'open-settings'])

const markingTaskId = ref(null)
const completedTasks = ref(new Set()) // Track completed tasks for UI state
const fadingTasks = ref(new Set()) // Track tasks that are fading out
const lastCompletedTask = ref(null) // Store last completed task for undo
// Global snackbar
const { show: showSnackbar } = useSnackbar()

// Query to get current user
const currentUserQuery = `
  query GetCurrentUser {
    me {
      user {
        name
        surname
        fullname
        user_id
      }
    }
  }
`

// Fetch current user
const { 
  data: userData, 
  loading: loadingUser, 
  error: userError,
  cleanup: cleanupUser
} = useGraphQLData({
  dataKey: 'currentUser',
  query: currentUserQuery,
  variables: {},
  operationName: 'GetCurrentUser',
  priority: props.priority ?? 1, // Use provided priority or default to 1
  autoFetch: true
})

// Get user ID from user data
const userId = computed(() => {
  return userData.value?.me?.user?.user_id || null
})

// Query to get tasks
const tasksQuery = `
  query GetTasks($assigneeUserId: [ID!]!) {
    tasks(
      order: DUE_TIMESTAMP_ASC
      search: {
        assignee_user_id: $assigneeUserId
        include_unassigned: false
        status: TO_DO
      }
    ) {
      total
      found_without_filters
      data {
        task_id
        name
        assignee {
          user_id
          fullname
        }
        status
        due_timestamp
        is_due
      }
    }
  }
`

// Fetch tasks when user ID is available (or when in demo mode)
const demoMode = useDemoMode()
const { 
  data: tasksData, 
  loading, 
  error, 
  cleanup, 
  refresh 
} = useGraphQLData({
  dataKey: 'tasks',
  query: tasksQuery,
  variables: computed(() => {
    if (!userId.value) {
      return { assigneeUserId: [] }
    }
    return {
      assigneeUserId: [userId.value]
    }
  }),
  operationName: 'GetTasks',
  priority: props.priority ?? 1,
  autoFetch: false,
  getDemoData: () => getDemoTasksData()
})

// Watch for userId and fetch tasks when available; also fetch when demo mode turns on
watch([userId, () => demoMode?.value], ([newUserId, isDemoMode]) => {
  if (newUserId || isDemoMode) {
    refresh(true)
  }
}, { immediate: true })

// Get tasks list
const tasks = computed(() => {
  if (!tasksData.value?.tasks?.data) {
    return []
  }
  return tasksData.value.tasks.data
})

// Display max 3 tasks in the widget
const displayedTasks = computed(() => tasks.value.slice(0, 3))

// Get task count
const taskCount = computed(() => {
  return tasks.value.length
})

// Check if a task is past due
const isPastDue = (timestamp) => {
  if (!timestamp) return false
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const taskDate = new Date(timestamp * 1000)
  const taskDateOnly = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate())
  
  return taskDateOnly < today
}

// Count past due tasks
const pastDueCount = computed(() => {
  return tasks.value.filter(task => task.due_timestamp && isPastDue(task.due_timestamp)).length
})

// Format due date (and time if present)
const formatDueDate = (timestamp) => {
  if (!timestamp) return 'No due date'
  
  const date = new Date(timestamp * 1000) // Convert from seconds to milliseconds
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const taskDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  
  const diffTime = taskDate - today
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  let dateStr
  if (diffDays === 0) {
    dateStr = 'Today'
  } else if (diffDays === 1) {
    dateStr = 'Tomorrow'
  } else if (diffDays === -1) {
    dateStr = 'Yesterday'
  } else if (diffDays > 0 && diffDays <= 7) {
    dateStr = `In ${diffDays} days`
  } else if (diffDays < 0 && diffDays >= -7) {
    dateStr = `${Math.abs(diffDays)} days ago`
  } else {
    dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined })
  }

  // Add time if it's not midnight
  const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0
  if (hasTime) {
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    return `${dateStr} @ ${timeStr}`
  }
  return dateStr
}

// Mutation to mark task as done
const markTaskAsDone = async (task) => {
  if (markingTaskId.value === task.task_id || completedTasks.value.has(task.task_id)) {
    return // Already marking this task or already completed
  }
  
  markingTaskId.value = task.task_id
  
  try {
    // Use tasksCommit mutation without organization_id
    const mutation = `
      mutation TasksCommit($task: TaskSearchInput, $input: TaskInput) {
        tasksCommit(task: $task, input: $input) {
          total
        }
      }
    `
    
    const variables = {
      task: {
        task_id: task.task_id
      },
      input: {
        status: 'COMPLETED'
      }
    }
    
    const response = await executeGraphQLQuery(mutation, variables, 'TasksCommit')
    
    // executeGraphQLQuery returns { data, headers }
    // response.data is the GraphQL response which has { data: {...}, errors: [...] }
    const graphqlResponse = response.data || response
    
    // Check for GraphQL errors
    if (graphqlResponse && graphqlResponse.errors) {
      console.error('❌ GraphQL errors:', graphqlResponse.errors)
      throw new Error(`GraphQL Error: ${JSON.stringify(graphqlResponse.errors)}`)
    }
    
    // The actual data is in graphqlResponse.data
    const result = graphqlResponse.data || graphqlResponse
    
    
    // Check if mutation was successful - tasksCommit should be in result
    if (!result || !result.tasksCommit) {
      console.error('❌ Mutation did not return expected result:', result)
      console.error('❌ Available fields:', Object.keys(result))
      // If we got here without errors, the mutation probably succeeded
      // Just log a warning and continue
      console.warn('⚠️ tasksCommit field not found, but no errors - assuming success')
    } else {
    }
    
    // Mark as completed for UI
    completedTasks.value.add(task.task_id)
    lastCompletedTask.value = { ...task } // Store for undo
    
    // Show global snackbar with undo option
    showSnackbar('Task completed', { undo: undoTaskCompletion })
    
    // After 2 seconds, fade out the task
    setTimeout(() => {
      fadingTasks.value.add(task.task_id)
      
      // After fade animation completes, remove from list
      setTimeout(() => {
        completedTasks.value.delete(task.task_id)
        fadingTasks.value.delete(task.task_id)
        // Refresh tasks list to remove completed task
        // Only refresh if task was successfully updated
        refresh(true)
      }, 300) // Match CSS transition duration
    }, 2000)
    
  } catch (err) {
    console.error('❌ Error marking task as done:', err)
    console.error('❌ Error details:', {
      message: err.message,
      response: err.response,
      stack: err.stack
    })
    
    // Remove from completed state if it was added
    completedTasks.value.delete(task.task_id)
    fadingTasks.value.delete(task.task_id)
    
            showSnackbar('Failed to update task')
    markingTaskId.value = null
  } finally {
    // Don't clear markingTaskId immediately - let UI show completed state
    setTimeout(() => {
      markingTaskId.value = null
    }, 100)
  }
}

// Undo task completion
const undoTaskCompletion = async () => {
  if (!lastCompletedTask.value) {
    return
  }
  
  const task = lastCompletedTask.value
  
  try {
    // Use tasksCommit mutation to set back to TO_DO (without organization_id)
    const mutation = `
      mutation TasksCommitUndo($task: TaskSearchInput, $input: TaskInput) {
        tasksCommit(task: $task, input: $input) {
          total
        }
      }
    `
    
    const variables = {
      task: {
        task_id: task.task_id
      },
      input: {
        status: 'TO_DO'
      }
    }
    
    await executeGraphQLQuery(mutation, variables, 'TasksCommitUndo')
    
    // Remove from completed/fading sets
    completedTasks.value.delete(task.task_id)
    fadingTasks.value.delete(task.task_id)
    
    // Refresh tasks list to show task again
    await refresh(true)
    
    // Clear last completed task
    lastCompletedTask.value = null
    
    // Show success message
            showSnackbar('Task restored')
  } catch (err) {
    console.error('Error undoing task completion:', err)
            showSnackbar('Failed to restore task')
  }
}

// Extract URL parameters for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || undefined
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  
  // If not found in current URL, try referrer
  if (!clientId || !canvasUUID) {
    try {
      const referrer = document.referrer
      if (referrer) {
        const referrerUrl = new URL(referrer)
        const referrerParams = new URLSearchParams(referrerUrl.search)
        if (!clientId) {
          clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || undefined
        }
        if (!canvasUUID) {
          canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
        }
        if (clientId || canvasUUID) {
        }
      }
    } catch (err) {
      // Referrer might not be accessible
    }
  }
  
  // If still not found, try parent window URL (if accessible)
  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window && window.parent.location) {
        const parentUrl = new URL(window.parent.location.href)
        const parentParams = new URLSearchParams(parentUrl.search)
        if (!clientId) {
          clientId = parentParams.get('client_id') || parentParams.get('clientId') || undefined
        }
        if (!canvasUUID) {
          canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
        }
        if (clientId || canvasUUID) {
        }
      }
    } catch (err) {
      // Parent URL might not be accessible due to cross-origin restrictions
    }
  }
  
  return {
    client_id: clientId,
    canvasUUID: canvasUUID
  }
}

// Handle task row click - open task edit dialog
const { guard } = useTrialModeGuard()
const handleTaskClickGuarded = guard((task) => {
  if (!task.task_id) {
    console.warn('⚠️ Task ID not found:', task)
    return
  }
  
  const params = getUrlParams()
  
  // Try to get canvasUUID from iframe name (Optix might set this)
  let canvasUUID = params.canvasUUID
  if (!canvasUUID && window.name) {
    // Check if window.name matches the canvasUUID format
    const nameMatch = window.name.match(/^\d+-\d+$/)
    if (nameMatch) {
      canvasUUID = window.name
    }
  }
  
  // Generate canvasUUID in Optix format if still not found: {timestamp}-{random}
  // Format: 1770323837864-528326 (13-digit timestamp + dash + 6-digit random)
  const generateCanvasUUID = () => {
    const timestamp = Date.now()
    const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
    return `${timestamp}-${random}`
  }
  
  const finalCanvasUUID = canvasUUID || generateCanvasUUID()
  
  
  // Send TaskEdit message to parent window
  // Try both formats - plain object (as shown in Optix logs) and command structure (like other widgets)
  const messageData = {
    client_id: params.client_id,
    task_id: task.task_id.toString(),
    canvasUUID: finalCanvasUUID
  }
  
  // Format 1: Plain object (matches Optix console log format)
  const plainMessage = messageData
  
  // Format 2: Command structure (like ViewDetails for bookings/assignments)
  const commandMessage = {
    command: 'TaskEdit',
    payload: messageData
  }
  
  
  // Send message to parent window
  // Try both formats - Optix might expect command structure even though logs show plain object
  try {
    if (window.parent && window.parent !== window) {
      // Send plain format first (matches console logs)
      window.parent.postMessage(plainMessage, '*')
      
      // Also try command format (like other working widgets)
      window.parent.postMessage(commandMessage, '*')
    } else {
      console.warn('⚠️ No parent window found - not in iframe?')
      console.warn('⚠️ This widget needs to run inside an Optix iframe to open task dialogs')
    }
  } catch (err) {
    console.error('❌ Error posting TaskEdit message:', err)
  }
})

// Handle "View all" click - navigate to tasks page via parent window
// Uses same method as UpcomingBookingsWidget (ReplaceUrl postMessage)
const handleViewAllClick = () => {
  const params = getUrlParams()
  let clientId = params.client_id
  let canvasUUID = params.canvasUUID

  // canvasUUID is required - use fallback if not found
  const finalCanvasUUID = canvasUUID || "default-canvas-uuid"
  const navigationUrl = "/tasks"

  const message = {
    command: 'ReplaceUrl',
    payload: {
      client_id: clientId || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (err) {
    console.error('❌ Error posting ReplaceUrl message:', err)
  }
}

onUnmounted(() => {
  cleanup()
  cleanupUser()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
  pointer-events: auto;
  position: relative;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.tasks-widget-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  flex-shrink: 0;
  padding-top: 4px;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.view-all-link:hover {
  color: #2563eb;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.tasks-table {
  margin-top: 0;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  max-height: 100%;
  padding: 0;
  pointer-events: auto;
}

.tasks-table table {
  width: 100%;
  border-collapse: collapse;
}



.tasks-table tbody tr {
  height: 64px;
  border-bottom: 1px solid #e5e7eb;
}

.tasks-table tbody tr:last-child {
  border-bottom: none;
}

.task-row {
  cursor: pointer;
  transition: background-color 0.2s;
  pointer-events: auto;
}

.task-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.checkbox-cell {
  width: 40px;
  padding: 12px 0;
}

.task-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: rgba(0, 0, 0, 0.87);
  transition: color 0.2s;
}

.task-checkbox:hover {
  color: rgba(0, 0, 0, 0.87);
}

.task-checkbox:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.checkbox-icon {
  font-size: 24px;
  line-height: 1;
  transition: all 0.2s;
}

.checkbox-checked {
  font-size: 24px;
  color: var(--color-green); /* Green for filled checkmark */
  transform: scale(0.85); /* Make the checkmark appear smaller inside the circle */
}

.task-completed .checkbox-icon {
  color: var(--color-green);
}

.task-name-cell {
  padding: 12px 0;
  transition: all 0.3s;
}

.task-name {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
}

.task-due-date {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
  margin-top: 2px;
}

.task-due-date.past-due {
  color: #CB912F; /* Same as comparison chip yellow */
  font-weight: 500;
}

.task-strikethrough {
  text-decoration: line-through;
  opacity: 0.6;
}

.task-fading {
  opacity: 0;
  transform: translateX(-10px);
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
}

.task-row {
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
}

.no-tasks {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.past-due-text {
  font-size: 14px;
  font-weight: 500;
  color: #CB912F; /* Same as comparison chip yellow */
}
</style>
